(function () {
  const STORAGE_KEY = 'nude-recently-viewed-products';
  const MAX_STORED_PRODUCTS = 16;
  const RECENT_CARD_SECTION_ID = 'predictive-search';

  function storeRecentlyViewed(productTitle) {
    if (!productTitle) return;

    try {
      const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      const next = [productTitle, ...existing.filter((item) => item !== productTitle)].slice(0, MAX_STORED_PRODUCTS);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      console.warn('Unable to store recently viewed products.', error);
    }
  }

  function getRecentlyViewedTitles(currentProductTitle) {
    try {
      const titles = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return titles.filter((title) => title && title !== currentProductTitle);
    } catch (error) {
      console.warn('Unable to read recently viewed products.', error);
      return [];
    }
  }

  class TabbedProductRail extends HTMLElement {
    connectedCallback() {
      this.productTitle = this.dataset.productTitle;
      this.tabs = Array.from(this.querySelectorAll('[data-tab-target]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.track = this.querySelector('[data-recently-viewed-track]');
      this.emptyState = this.querySelector('[data-recently-viewed-empty]');
      this.railSwipers = new Map();

      this.initializeTabs();
      this.initializeRailSwipers();
      this.loadRecentlyViewed();
    }

    initializeTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.tabTarget));
      });
    }

    activateTab(target) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tabTarget === target;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.panel === target;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      const swiper = this.railSwipers.get(target);
      if (swiper) swiper.update();
    }

    // Re-init a single rail after its slides were replaced externally
    // (product-recommendations grafts fresh similar-rail slides on lazy load)
    refreshRail(rail) {
      const swiper = this.railSwipers.get(rail);
      if (swiper) {
        swiper.destroy(true, true);
        this.railSwipers.delete(rail);
      }
      this.initializeRailSwipers();

      const activeSwiper = this.railSwipers.get(rail);
      if (activeSwiper) activeSwiper.update();
    }

    initializeRailSwipers() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      this.querySelectorAll('[data-rail-swiper]').forEach((element) => {
        const rail = element.dataset.railSwiper;
        const prevEl = this.querySelector(`[data-rail-prev="${rail}"]`);
        const nextEl = this.querySelector(`[data-rail-next="${rail}"]`);

        if (this.railSwipers.has(rail)) return;

        if (!element.querySelector('.swiper-slide')) {
          prevEl?.classList.add('is-hidden');
          nextEl?.classList.add('is-hidden');
          return;
        }

        if (isMobile) {
          // Swiper's JS touch handling fights the page's native scroll on Android;
          // below the phone breakpoint, skip Swiper entirely and let the browser
          // scroll the rail natively instead.
          element.classList.add('related-products__swiper--native-scroll');
          prevEl?.classList.add('is-hidden');
          nextEl?.classList.add('is-hidden');
          return;
        }

        prevEl?.classList.remove('is-hidden');
        nextEl?.classList.remove('is-hidden');

        const swiper = new Swiper(element, {
          slidesPerView: 'auto',
          spaceBetween: 1,
          simulateTouch: true,
          threshold: 0,
          touchAngle: 70,
          freeMode: {
            enabled: true,
            sticky: false,
          },
          mousewheel: { forceToAxis: true },
          navigation: {
            prevEl,
            nextEl,
          },
        });

        this.railSwipers.set(rail, swiper);
      });
    }

    async loadRecentlyViewed() {
      if (!this.track) return;

      const titles = getRecentlyViewedTitles(this.productTitle);

      if (!titles.length) {
        this.emptyState?.removeAttribute('hidden');
        return;
      }

      const cards = await Promise.all(titles.map((title) => this.fetchRecentProductCard(title)));
      const validCards = cards.filter(Boolean);

      if (!validCards.length) {
        this.emptyState?.removeAttribute('hidden');
        return;
      }

      this.track.innerHTML = validCards
        .map(
          (cardMarkup) => `
            <div class="swiper-slide related-products__slide">
              ${cardMarkup}
            </div>
          `
        )
        .join('');

      this.emptyState?.setAttribute('hidden', '');

      const recentRail = this.railSwipers.get('recent');
      if (recentRail) {
        recentRail.destroy(true, true);
        this.railSwipers.delete('recent');
      }

      this.initializeRailSwipers();
    }

    async fetchRecentProductCard(productTitle) {
      try {
        const response = await fetch(
          `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(productTitle)}&section_id=${RECENT_CARD_SECTION_ID}&resources[type]=product&resources[limit]=1&resources[options][unavailable_products]=last`
        );

        if (!response.ok) return null;

        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const section = doc.querySelector(`#shopify-section-${RECENT_CARD_SECTION_ID}`);
        const productCard = section?.querySelector('product-card');

        if (!productCard || productCard.dataset.recsAllowed === 'false') return null;

        return productCard.outerHTML;
      } catch (error) {
        console.warn(`Unable to load recently viewed product card for "${productTitle}".`, error);
        return null;
      }
    }
  }

  if (!customElements.get('tabbed-product-rail')) {
    customElements.define('tabbed-product-rail', TabbedProductRail);
  }

  function trackVisibleProducts() {
    document.querySelectorAll('product-recommendations.related-products[data-product-title]').forEach((section) => {
      storeRecentlyViewed(section.dataset.productTitle);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackVisibleProducts, { once: true });
  } else {
    trackVisibleProducts();
  }
})();
